/*
Sample-data substitution for the block builder's proof.

WHY THIS EXISTS. Off the block path the proof is the SERVER's render:
previewEmailTemplate (ps-back/graphql/resolvers/queries/admin/previewEmailTemplate.js)
runs the template through the same TemplateRenderer that SES uses, over
`{ ...baselineSampleData(usedVariables), ...sampleData }` — synthesised
`VAR_<name>` stand-ins for every variable the template uses, with anything the
editor's Sample data box supplies overriding them.

The block builder renders locally and never calls that resolver, so its proof
showed `{{firstName}}` literally while the cell editor on the very same screen
showed a substituted value. That is a step backwards on one screen, so this
does the same substitution the server path does, over the same data, with the
same precedence.

WHAT THIS IS NOT. It is not Handlebars and must never grow into a second
template engine — two implementations would drift, and the one that matters is
the one that sends the email. It substitutes plain variable tokens and leaves
everything else exactly as written:

  {{{name}}}   raw, unescaped (78 of the live templates use triple-stache)
  {{name}}     HTML-escaped, exactly as Handlebars escapes it
  {{#if x}}    left alone — a section is control flow, and pretending to
  {{/if}}      evaluate one would show a proof the server would not produce
  {{!…}} {{>…}} {{&…}}  left alone for the same reason

It is used ONLY for what the proof pane displays. The saved HTML, the string
the mutation sends and the variable check all read the un-substituted render,
so nothing here can put `VAR_firstName` into a sent email.

CommonJS on purpose: it runs under plain `node src/utils/emailSampleRender.test.js`.
*/

const { extractVariables } = require('./emailDraft');

// Ported from ps-back/util/email/normalizeHtml.js. The VAR_ convention is also
// what scripts/emails/recordBaseline.js uses, so a proof here reads the same as
// a recorded baseline.
const baselineSampleData = (variables = []) => variables.reduce(
  (data, name) => ({ ...data, [name]: `VAR_${name}` }),
  {},
);

// Handlebars' own escapeExpression set, character for character. Anything less
// and a proof would differ from the send over an apostrophe in a first name.
const ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

const escapeExpression = value => String(value).replace(/[&<>"'`=]/g, char => ESCAPES[char]);

/*
Parses the editor's Sample data box, defensively.

Mirrors previewEmailTemplate's own JSON.parse: bad JSON is not an exception
here. The editor already reports INVALID_SAMPLE_DATA_JSON through validateDraft
and blocks the save on it; the proof's job while that is being fixed is to keep
rendering with the baseline placeholders rather than to blank out.
*/
const parseSampleData = (sampleDataJson) => {
  if (!sampleDataJson || !String(sampleDataJson).trim()) return {};
  try {
    const parsed = JSON.parse(sampleDataJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

// `{{user.firstName}}` resolves through the object the way Handlebars would,
// and a flat key of exactly that name wins first — sample data pasted as
// `{"user.firstName": "Jo"}` is a real thing people do, and Handlebars itself
// would not find it, so this is the one place that is deliberately kinder than
// the send.
const lookup = (data, name) => {
  if (Object.prototype.hasOwnProperty.call(data, name)) return data[name];
  const parts = name.split('.');
  let current = data;
  for (let index = 0; index < parts.length; index += 1) {
    if (!current || typeof current !== 'object') return undefined;
    current = current[parts[index]];
  }
  return current;
};

// null and undefined print as nothing, which is what Handlebars does. A number
// or a boolean prints as itself.
const stringify = value => (value === null || value === undefined ? '' : String(value));

// The same token shape emailDraft.js and ps-back/util/email/extractVariables.js
// scan for: `\{\{\{?` takes the opening braces and `\}?\}\}` gives the optional
// third one back. The captured group tells a triple-stache from a double.
const TOKEN = /\{\{(\{?)([^{}]+)\}?\}\}/g;
const NOT_A_VARIABLE = ['#', '/', '^', '!', '>', '&'];

/*
The proof's HTML: `html` with every variable token replaced by its sample value.

The supplied sample data is consulted FIRST and the synthesised placeholder is
only the fallback — the same precedence the server's
`{ ...baselineSampleData(used), ...sampleData }` produces. Two separate objects
rather than one merge because `{{user.firstName}}` has to traverse into the
supplied `user` object, and a merged baseline would shadow it with a flat
`'user.firstName'` key that Handlebars would never have looked at.

So supplying only `firstName` substitutes that one and leaves the rest reading
`VAR_<name>` — which is what makes a missing variable visible instead of
silently rendering an empty gap.
*/
const substituteSampleData = (html, sampleDataJson) => {
  if (!html) return html;

  const used = extractVariables(html);
  if (used.length === 0) return html;

  const baseline = baselineSampleData(used);
  const sample = parseSampleData(sampleDataJson);

  TOKEN.lastIndex = 0;
  return html.replace(TOKEN, (match, triple, raw) => {
    const name = raw.trim().split(/[\s|]/)[0];
    // A section, a comment, a partial, or a token with arguments after the
    // name. Handlebars would do something here that this deliberately does not
    // model, so the proof shows it as written rather than guessing.
    if (!name || NOT_A_VARIABLE.includes(name[0]) || name !== raw.trim()) return match;

    const supplied = lookup(sample, name);
    const value = supplied === undefined ? baseline[name] : supplied;
    if (value === undefined) return match;
    return triple ? stringify(value) : escapeExpression(stringify(value));
  });
};

module.exports = {
  baselineSampleData,
  parseSampleData,
  escapeExpression,
  substituteSampleData,
};
